import { useCallback, useState } from 'react';

import _ from 'lodash';
import { type PickOne } from 'src/types/utilities';

// **** Types **** //

export type ErrorRecord<T> = Partial<Record<keyof T, { error: boolean; errorMessage?: string }>>;
export type HandleChange<T extends object> = <U extends PickOne<T>, S>(pair: U, sanitizeFn?: (value: any) => S) => void;
export type HandleChangeDynamicKey<T extends object> = <K extends keyof T>(key: K, value: T[K]) => void;
export type UpdatePartial<T extends object> = (partial: Partial<T>) => void;
export type FormHandler<T> = ReturnType<typeof useForm<T>>;

export type Validations<T> = Partial<ValidationsHelper<T>>;

type ValidationsHelper<T> = {
  [K in keyof T]: Validation<T, K>;
};

interface Validation<T, K extends keyof T> {
  required?: {
    value: boolean;
    message?: string;
  };
  pattern?: {
    value: string;
    message?: string;
  };
  custom?: {
    isValid: (value: T[K], formData: T) => boolean;
    message?: string;
  };
}

export interface UseFormOptions<T, RO extends object = Readonly<T>> {
  validations?: Validations<RO>;
  initialValues?: Partial<RO>;
}

// **** Functions **** //

/**
 * Manage a form with multiple fields that need validation.
 */
const useForm = <T extends Partial<Record<keyof T, any>> = object>(options?: UseFormOptions<T>) => {
  const [formData, setFormData] = useState<T>((options?.initialValues ?? {}) as T);
  const [errors, setErrors] = useState<ErrorRecord<T>>({});

  // Change handler with an optional sanitizeFn to change format of data
  const handleChange = useCallback(
    <S>(pair: PickOne<T>, sanitizeFn?: (value: any) => S) => {
      const [key, value] = Object.entries(pair)[0];
      setFormData({
        ...formData,
        [key]: sanitizeFn ? sanitizeFn(value) : value,
      });
      if (errors[key as keyof T]) setErrors({ ...errors, [key]: undefined });
    },
    [setFormData, formData],
  ) as HandleChange<T>;

  // Wrapper for the handle change function for when you have a dynamic key
  const handleChangeDynamicKey = <K extends keyof T>(key: K, value: T[K]) => {
    return handleChange({ [key]: value } as PickOne<T>);
  };

  // Change handler with an optional sanitizeFn to change format of data
  const updatePartial = useCallback(
    (partial: Partial<T>) => {
      setFormData({
        ...formData,
        ...partial,
      });
    },
    [setFormData, formData],
  );

  // Reset form to inital state
  const resetForm = useCallback(() => {
    setFormData((options?.initialValues ?? {}) as T);
    setErrors({});
  }, [options?.initialValues, setFormData, setErrors]);

  // Set all form values
  const setFormValues = useCallback((newFormValues: T) => setFormData(newFormValues), [setFormData]);

  // Reset field to initial state
  const resetFormField = (key: keyof T) =>
    handleChangeDynamicKey(key, options?.initialValues?.[key] || (undefined as T[keyof T]));

  // Submit handler
  const validateForm = useCallback(() => {
    const validations = options?.validations;
    if (!validations) {
      return true;
    }
    const result = runValidations(formData, validations);
    setErrors(result.errors);
    if (!result.valid) {
      return false;
    }
    return true;
  }, [options?.validations, formData, setErrors]);

  // Return
  return {
    formData,
    handleChange,
    handleChangeDynamicKey,
    updatePartial,
    validateForm,
    resetForm,
    resetFormField,
    setFormValues,
    errors,
  };
};

/**
 * Run validations against form data.
 */
const runValidations = <T extends Record<keyof T, any>>(
  formData: T,
  validations: Validations<T>,
): { errors: ErrorRecord<T>; valid: boolean } => {
  let valid = true;
  const newErrors: ErrorRecord<T> = {};
  for (const key in validations) {
    newErrors[key] = {
      error: false,
      errorMessage: undefined,
    };
    const value = formData[key];
    const validation = validations[key];
    if (
      validation?.required?.value &&
      (_.isUndefined(value) ||
        (_.isArrayLike(value) && _.isEmpty(value)) ||
        (typeof value == 'string' && !value.trim()))
    ) {
      valid = false;
      newErrors[key] = {
        error: true,
        errorMessage: validation?.required?.message,
      };
    }
    const pattern = validation?.pattern;
    if (pattern?.value && !RegExp(pattern.value).test(value)) {
      valid = false;
      newErrors[key] = { error: true, errorMessage: pattern.message || '' };
    }
    const custom = validation?.custom;
    if (custom?.isValid && !custom.isValid(value, formData)) {
      valid = false;
      newErrors[key] = { error: true, errorMessage: custom.message || '' };
    }
  }
  return { errors: newErrors, valid };
};

// **** Export **** //

export default useForm;
